/**
 * GatewayDemoLaunchers — the single workflow-demo showcase that sits BETWEEN the
 * Ari Hero and the 4 Gateway access cards.
 *
 * 2026-06-12 (approved): consolidated from the 4 role-based demo buttons
 * (Customer/Provider/Network Contractor/Tools) + the "Recorded demo" pill into
 * ONE canonical demo — RemodDash is one connected workflow powered by Ari, not
 * separate products. Compact video-thumbnail strip → opens the full workflow
 * walkthrough in a modal/lightbox. Position, surrounding spacing, the Ari Hero
 * and the access cards are unchanged.
 */
import React, { useState, useEffect, useRef } from 'react';
import { Clapperboard, Play, Pause, X, RotateCcw, RotateCw, Maximize, Minimize } from 'lucide-react';

const ASSET = (p) => `${process.env.PUBLIC_URL || ''}${p}`;
// 2026-06-14: wired the real full 15-stage lifecycle walkthrough (≈2:25) that runs
// intake → routing → accept → schedule → assign → en route → arrive → diagnosis →
// estimate → approval → work → complete → payment → paid invoice → Ari closeout.
// Distinct filename (not remoddash_walkthrough.mp4, the old 0:54 clip) so the live
// asset is unambiguous and never masked by a cached older file.
const VIDEO_SRC = ASSET('/demo/lifecycle_walkthrough.mp4');
const POSTER_PROVIDER = ASSET('/demo/poster_provider.png');
const POSTER_CUSTOMER = ASSET('/demo/poster_customer.png');

// P3 Gateway demo player — custom controls (owner spec 2026-06-14):
// play/pause · seekable progress bar · rewind 10s · forward 10s ·
// two-way speed (0.5×–1.5×) · NO autoplay · NO loop. Chapter markers / caption
// strip intentionally deferred until the final demo video + its in-video
// timestamps are locked by the owner (avoid building markers against a draft cut).
const SPEEDS = [0.5, 0.75, 1, 1.25, 1.5];

const fmtTime = (s) => {
  if (!s || !isFinite(s)) return '0:00';
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, '0')}`;
};

const DemoVideoPlayer = () => {
  const videoRef = useRef(null);
  const playerRef = useRef(null);
  const menuRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [current, setCurrent] = useState(0);
  const [duration, setDuration] = useState(0);
  const [rate, setRate] = useState(0.75); // default slower so viewers can follow the workflow
  const [speedOpen, setSpeedOpen] = useState(false);
  const [menuShift, setMenuShift] = useState(0);
  const [isFs, setIsFs] = useState(false);

  // Track fullscreen state (so the button icon + centered layout stay in sync).
  useEffect(() => {
    const onFs = () => setIsFs(!!(document.fullscreenElement || document.webkitFullscreenElement));
    document.addEventListener('fullscreenchange', onFs);
    document.addEventListener('webkitfullscreenchange', onFs);
    return () => {
      document.removeEventListener('fullscreenchange', onFs);
      document.removeEventListener('webkitfullscreenchange', onFs);
    };
  }, []);

  // Keep the speed menu fully inside the player on any screen width (mobile-safe):
  // after it opens, measure it and nudge it left/right if it would clip an edge.
  useEffect(() => {
    if (!speedOpen) { setMenuShift(0); return; }
    const id = requestAnimationFrame(() => {
      const el = menuRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const pl = playerRef.current ? playerRef.current.getBoundingClientRect() : null;
      const rightBound = Math.min(pl ? pl.right : window.innerWidth, window.innerWidth) - 8;
      const leftBound = Math.max(pl ? pl.left : 0, 0) + 8;
      let shift = 0;
      if (r.right > rightBound) shift = rightBound - r.right;
      if (r.left + shift < leftBound) shift = leftBound - r.left;
      setMenuShift(Math.round(shift));
    });
    return () => cancelAnimationFrame(id);
  }, [speedOpen]);

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) v.play(); else v.pause();
  };
  const skip = (delta) => {
    const v = videoRef.current;
    if (!v) return;
    v.currentTime = Math.min(Math.max(0, v.currentTime + delta), v.duration || 0);
  };
  const onSeek = (e) => {
    const v = videoRef.current;
    const t = Number(e.target.value);
    if (v) v.currentTime = t;
    setCurrent(t);
  };
  const setSpeed = (r) => {
    const v = videoRef.current;
    if (v) v.playbackRate = r;
    setRate(r);
    setSpeedOpen(false);
  };
  // Full screen — expands the whole player (video + custom controls). Falls back
  // to native video fullscreen on iOS Safari (no element-fullscreen support).
  const toggleFullscreen = () => {
    const fsEl = document.fullscreenElement || document.webkitFullscreenElement;
    if (fsEl) {
      if (document.exitFullscreen) document.exitFullscreen();
      else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
      return;
    }
    const el = playerRef.current;
    if (el && el.requestFullscreen) { el.requestFullscreen().catch(() => {}); return; }
    if (el && el.webkitRequestFullscreen) { el.webkitRequestFullscreen(); return; }
    const v = videoRef.current; // iOS Safari fallback
    if (v && v.webkitEnterFullscreen) { v.webkitEnterFullscreen(); }
  };

  return (
    <div
      ref={playerRef}
      className={`bg-black ${isFs ? 'h-full w-full flex flex-col' : ''}`}
      data-testid="demo-player"
    >
      <div className={isFs ? 'flex-1 min-h-0 flex items-center justify-center overflow-hidden' : ''}>
        <video
          ref={videoRef}
          data-testid="demo-video"
          playsInline
          preload="metadata"
          poster={POSTER_CUSTOMER}
          className={`block bg-black cursor-pointer ${isFs ? 'max-w-full max-h-full w-auto h-auto object-contain' : 'w-full'}`}
          onClick={togglePlay}
          onPlay={() => { setPlaying(true); const v = videoRef.current; if (v) v.playbackRate = rate; }}
          onPause={() => setPlaying(false)}
          onTimeUpdate={() => setCurrent(videoRef.current?.currentTime || 0)}
          onLoadedMetadata={() => {
            const v = videoRef.current;
            if (v) { setDuration(v.duration || 0); v.playbackRate = rate; }
          }}
          onEnded={() => setPlaying(false)}
        >
          <source src={VIDEO_SRC} type="video/mp4" />
        </video>
      </div>

      {/* Custom control bar — NO autoplay, NO loop */}
      <div className="flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 py-2.5" style={{ background: '#0b0922' }} data-testid="demo-controls">
        <button
          type="button"
          onClick={togglePlay}
          data-testid="demo-play-toggle"
          aria-label={playing ? 'Pause' : 'Play'}
          className="flex-none w-9 h-9 grid place-items-center rounded-full text-white transition-transform hover:scale-105"
          style={{ background: 'rgba(168,85,247,0.95)' }}
        >
          {playing ? <Pause className="w-4 h-4 fill-current" /> : <Play className="w-4 h-4 fill-current ml-0.5" />}
        </button>

        <button
          type="button"
          onClick={() => skip(-10)}
          data-testid="demo-rewind-10"
          aria-label="Rewind 10 seconds"
          className="flex-none relative w-8 h-8 grid place-items-center rounded-lg text-white/85 hover:text-white hover:bg-white/10 transition-colors"
        >
          <RotateCcw className="w-4 h-4" />
          <span className="absolute text-[7px] font-bold">10</span>
        </button>
        <button
          type="button"
          onClick={() => skip(10)}
          data-testid="demo-forward-10"
          aria-label="Forward 10 seconds"
          className="flex-none relative w-8 h-8 grid place-items-center rounded-lg text-white/85 hover:text-white hover:bg-white/10 transition-colors"
        >
          <RotateCw className="w-4 h-4" />
          <span className="absolute text-[7px] font-bold">10</span>
        </button>

        <input
          type="range"
          min={0}
          max={duration || 0}
          step="0.1"
          value={current}
          onChange={onSeek}
          data-testid="demo-progress"
          aria-label="Seek"
          className="flex-1 min-w-0 h-1.5 cursor-pointer"
          style={{ accentColor: '#a855f7' }}
        />

        <span className="flex-none text-[11px] sm:text-[12px] tabular-nums text-white/80 min-w-[58px] sm:min-w-[80px] text-right" data-testid="demo-time">
          {fmtTime(current)} / {fmtTime(duration)}
        </span>

        <div className="relative flex-none">
          <button
            type="button"
            onClick={() => setSpeedOpen((s) => !s)}
            data-testid="demo-speed-btn"
            aria-label="Playback speed"
            className="px-2.5 h-8 rounded-lg text-[12px] font-bold text-white/90 hover:bg-white/10 transition-colors"
          >
            {rate}×
          </button>
          {speedOpen && (
            <div
              ref={menuRef}
              className="absolute bottom-full right-0 mb-1.5 rounded-lg overflow-hidden border shadow-lg"
              style={{ background: '#15123a', borderColor: 'rgba(255,255,255,0.12)',
                       transform: menuShift ? `translateX(${menuShift}px)` : undefined }}
              data-testid="demo-speed-menu"
            >
              {SPEEDS.map((r) => (
                <button
                  key={r}
                  type="button"
                  onClick={() => setSpeed(r)}
                  data-testid={`demo-speed-${r}`}
                  className={`block w-full px-5 py-1.5 text-[12px] text-right transition-colors ${r === rate ? 'text-purple-300 font-bold bg-white/10' : 'text-white/80 hover:bg-white/10'}`}
                >
                  {r}×
                </button>
              ))}
            </div>
          )}
        </div>

        <button
          type="button"
          onClick={toggleFullscreen}
          data-testid="demo-fullscreen-btn"
          aria-label={isFs ? 'Exit full screen' : 'Full screen'}
          className="flex-none w-8 h-8 grid place-items-center rounded-lg text-white/85 hover:text-white hover:bg-white/10 transition-colors"
        >
          {isFs ? <Minimize className="w-4 h-4" /> : <Maximize className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
};

const GatewayDemoLaunchers = () => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    if (open) window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  return (
    <div className="max-w-5xl mx-auto mb-8" data-testid="gateway-demo-launchers">
      <div
        className="max-w-2xl mx-auto flex flex-col sm:flex-row items-stretch sm:items-center gap-4 rounded-2xl p-3.5 border"
        data-testid="demo-showcase"
        style={{
          borderColor: 'rgba(168,85,247,0.30)',
          background: 'linear-gradient(160deg, rgba(124,58,237,0.14), rgba(99,102,241,0.05))',
          boxShadow: '0 16px 44px -22px rgba(124,58,237,0.7), inset 0 1px 0 rgba(255,255,255,0.05)',
        }}
      >
        {/* Thumbnail */}
        <button
          type="button"
          onClick={() => setOpen(true)}
          data-testid="demo-play-btn"
          aria-label="Play the full workflow demo"
          className="group relative flex-none w-full sm:w-56 aspect-video rounded-xl overflow-hidden cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-400"
          style={{ background: '#0c0a26' }}
        >
          <span className="absolute inset-0 flex">
            <img src={POSTER_PROVIDER} alt="" aria-hidden="true" className="w-1/2 h-full object-cover object-top" />
            <img src={POSTER_CUSTOMER} alt="" aria-hidden="true" className="w-1/2 h-full object-cover object-top" />
          </span>
          <span
            className="absolute inset-0"
            style={{ background: 'linear-gradient(180deg, rgba(10,8,32,0.05), rgba(10,8,32,0.55))' }}
          />
          <span className="absolute inset-0 grid place-items-center">
            <span
              className="w-12 h-12 rounded-full grid place-items-center transition-transform duration-200 group-hover:scale-110"
              style={{
                background: 'rgba(168,85,247,0.95)',
                boxShadow: '0 0 0 6px rgba(168,85,247,0.18), 0 10px 26px -6px rgba(168,85,247,0.9)',
              }}
            >
              <Play className="w-5 h-5 fill-current text-white ml-0.5" />
            </span>
          </span>
          <span className="absolute right-2 bottom-1.5 text-[10.5px] text-white px-1.5 py-0.5 rounded-md" style={{ background: 'rgba(0,0,0,0.55)' }}>
            Full workflow
          </span>
        </button>

        {/* Copy */}
        <div className="flex-1 text-center sm:text-left min-w-0">
          <div className="flex items-center justify-center sm:justify-start gap-1.5 text-[10.5px] tracking-[0.12em] uppercase mb-1.5" style={{ color: '#d7ccff' }}>
            <Clapperboard className="w-3.5 h-3.5" strokeWidth={2} />
            See RemodDash in action
          </div>
          <h2 className="text-base sm:text-lg font-extrabold leading-tight text-white">
            Watch Ari Run a Complete Job
          </h2>
          <p className="mt-1.5 text-[12.5px] leading-snug" style={{ color: '#cdc7ee' }}>
            See a real customer request move from intake, routing, scheduling, estimate, work completion, payment, and paid invoice.
          </p>
          <button
            type="button"
            onClick={() => setOpen(true)}
            data-testid="demo-watch-cta"
            className="mt-3 inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-[13px] font-bold text-white cursor-pointer transition-transform duration-200 hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-400"
            style={{
              background: 'linear-gradient(135deg, #7c3aed, #6366f1)',
              boxShadow: '0 10px 26px -10px rgba(124,58,237,0.9)',
            }}
          >
            <Play className="w-4 h-4 fill-current" />
            Watch Full Workflow Demo
          </button>
        </div>
      </div>

      {/* Lightbox */}
      {open && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center p-4"
          style={{ background: 'rgba(5,4,18,0.86)' }}
          data-testid="demo-video-modal"
          onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
        >
          <div className="w-full max-w-3xl rounded-2xl overflow-hidden border" style={{ background: '#0b0922', borderColor: 'rgba(255,255,255,0.10)' }}>
            <div className="flex justify-end px-2.5 py-2">
              <button
                type="button"
                onClick={() => setOpen(false)}
                data-testid="demo-modal-close"
                aria-label="Close demo"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[13px] text-white border cursor-pointer hover:bg-white/10 transition-colors"
                style={{ background: 'rgba(255,255,255,0.08)', borderColor: 'rgba(255,255,255,0.10)' }}
              >
                <X className="w-4 h-4" /> Close
              </button>
            </div>
            <DemoVideoPlayer />
          </div>
        </div>
      )}
    </div>
  );
};

export default GatewayDemoLaunchers;
